import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Users, AlertTriangle, RotateCcw, Copy, Check, X, Lock, Ban, Zap, Volume2, VolumeX, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BuzzerMode, BuzzerPress } from '../../../hooks/useBuzzerSignalR';
import { BUZZER_MODE_LABELS, BUZZER_MODES, BUZZER_SOUND_LABELS } from '../../../const/buzzer';
import BuzzerStage from './BuzzerStage';
import mainSignalSound from '../../../assets/sound/main-signal.mp3';
import preAlarmSound from '../../../assets/sound/pre-alarm.mp3';
import { playSoundCue, preloadSoundCues } from '../../../utils/soundCues';
import { useScoring } from '../../../hooks/useScoring';
import { getVerdict, nextToJudge, Verdict } from '../../../utils/scoring';
import { isFeatureEnabled } from '../../../services/flagsService';
import ScoringPanel from '../../Elements/Scoring/ScoringPanel';
import VerdictButtons from '../../Elements/Scoring/VerdictButtons';

interface BuzzerHostProps {
  gameCode: string;
  players: string[];
  presses: BuzzerPress[];
  lockedOutPlayers: string[];
  mode: BuzzerMode;
  hostConnected: boolean;
  onModeChange: (mode: BuzzerMode) => void;
  /** `silent` skips the confirmation toast (resets triggered by the point counter). */
  onResetAll: (silent?: boolean) => void;
  onResetOthers: (silent?: boolean) => void;
  onLeave: () => void;
}

const MUTE_STORAGE_KEY = '3sual.buzzer.muted';
const SCORING_STORAGE_KEY = '3sual.buzzer.scoring';

const readFlag = (key: string) => {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const writeFlag = (key: string, value: boolean) => {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch {
    // storage unavailable (private mode) — keep the in-memory value
  }
};

const panelStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '1rem',
};

const ghostBtnStyle: React.CSSProperties = {
  borderRadius: '0.75rem',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.10)',
};

const BuzzerHost: React.FC<BuzzerHostProps> = ({
  gameCode,
  players,
  presses,
  lockedOutPlayers,
  mode,
  hostConnected,
  onModeChange,
  onResetAll,
  onResetOthers,
  onLeave,
}) => {
  const [copied, setCopied] = useState(false);
  const previousPressCountRef = useRef<number | null>(null);
  const isLockout = mode === 'lockout';

  // Mute is a per-device preference, so it survives reloads of the buzzer.
  const [muted, setMuted] = useState<boolean>(() => readFlag(MUTE_STORAGE_KEY));

  const toggleMuted = useCallback(() => {
    setMuted(prev => {
      writeFlag(MUTE_STORAGE_KEY, !prev);
      return !prev;
    });
  }, []);

  // ── Point counter (opt-in) ────────────────────────────────────────────────
  // The buzzer works exactly as before while this is off; scores live on the
  // host only and survive toggling it off and on again.
  const scoringAvailable = isFeatureEnabled('BUZZER_SCORING');
  const [scoringPreferred, setScoringPreferred] = useState<boolean>(() => readFlag(SCORING_STORAGE_KEY));
  const scoringOn = scoringAvailable && scoringPreferred;
  const scoring = useScoring();

  const toggleScoring = useCallback(() => {
    setScoringPreferred(prev => {
      writeFlag(SCORING_STORAGE_KEY, !prev);
      return !prev;
    });
  }, []);

  // Whoever buzzed first and has not answered this question yet.
  const answeringPlayer = scoringOn ? nextToJudge(scoring.state, presses.map((p) => p.playerName)) : null;

  const handleVerdict = (playerName: string, verdict: Verdict) => {
    if (getVerdict(scoring.state, playerName)) return;
    scoring.judge(playerName, verdict);

    if (verdict === 'correct') {
      // Question closed — open the buzzer for the next one.
      onResetAll(true);
    } else if (isLockout) {
      // The wrong answerer stays out, everyone else may buzz for the same question.
      onResetOthers(true);
    }
    // Sequential: the next presser in the list is already waiting for a verdict.
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(gameCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    const previous = previousPressCountRef.current;
    previousPressCountRef.current = presses.length;

    if (previous === null) return;
    if (presses.length <= previous) return;
    if (muted) return;

    const latestPress = presses[presses.length - 1];
    const src = latestPress?.order === 1 ? mainSignalSound : preAlarmSound;

    playSoundCue(src);
  }, [presses, muted]);

  useEffect(() => {
    preloadSoundCues(mainSignalSound, preAlarmSound);
  }, []);

  return (
    <BuzzerStage>
      {/* ── Header ── */}
      <header className="relative z-10 flex flex-col gap-4 px-4 pb-3 pt-[calc(1.25rem+env(safe-area-inset-top))] sm:px-10 sm:pt-[calc(1.75rem+env(safe-area-inset-top))]">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            {/* Decorative — dropped on phones so the code and actions fit on one line. */}
            <div
              className="hidden h-9 w-9 shrink-0 items-center justify-center sm:flex sm:h-10 sm:w-10"
              style={{
                borderRadius: '0.75rem',
                background: 'rgba(56,189,248,0.10)',
                border: '1px solid rgba(56,189,248,0.18)',
              }}
            >
              <Zap className="h-4 w-4 text-sky-400 sm:h-5 sm:w-5" />
            </div>
            <div className="min-w-0">
              <p className="hidden text-[11px] font-bold uppercase tracking-widest text-sky-300/60 sm:block">Oyun Kodu</p>
              <div className="flex items-center gap-2">
                <span
                  aria-label={`Oyun kodu ${gameCode}`}
                  className="text-2xl font-black tracking-[0.2em] text-white sm:text-3xl"
                >
                  {gameCode}
                </span>
                <button
                  type="button"
                  onClick={handleCopy}
                  style={ghostBtnStyle}
                  className="flex h-8 w-8 items-center justify-center text-white/60 transition hover:text-white"
                  title="Kodu kopyala"
                  aria-label="Kodu kopyala"
                >
                  {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {scoringAvailable && (
              <button
                type="button"
                onClick={toggleScoring}
                aria-pressed={scoringOn}
                title={scoringOn ? 'Xal sayğacını bağla' : 'Xal sayğacını aç'}
                style={ghostBtnStyle}
                className={cn(
                  'flex h-9 items-center gap-1.5 px-3 text-sm font-semibold transition sm:h-10',
                  scoringOn ? 'text-amber-300 hover:text-amber-200' : 'text-white/60 hover:text-white'
                )}
              >
                <Trophy className="h-4 w-4" />
                <span className="hidden sm:inline">Xal sayğacı</span>
              </button>
            )}
            <button
              type="button"
              onClick={toggleMuted}
              aria-pressed={muted}
              title={muted ? BUZZER_SOUND_LABELS.muteOff : BUZZER_SOUND_LABELS.muteOn}
              aria-label={muted ? BUZZER_SOUND_LABELS.muteOff : BUZZER_SOUND_LABELS.muteOn}
              style={ghostBtnStyle}
              className={cn(
                'flex h-9 w-9 items-center justify-center transition sm:h-10 sm:w-10',
                muted ? 'text-amber-400 hover:text-amber-300' : 'text-white/60 hover:text-white'
              )}
            >
              {muted ? <VolumeX className="h-4 w-4 sm:h-5 sm:w-5" /> : <Volume2 className="h-4 w-4 sm:h-5 sm:w-5" />}
            </button>
            <button
              type="button"
              onClick={onLeave}
              title="Oyunu bitir"
              aria-label="Oyunu bitir"
              style={ghostBtnStyle}
              className="flex h-9 w-9 shrink-0 items-center justify-center gap-1.5 text-sm font-semibold text-white/60 transition hover:text-white sm:h-10 sm:w-auto sm:px-3"
            >
              <X className="h-4 w-4" />
              <span className="hidden sm:inline">Oyunu bitir</span>
            </button>
          </div>
        </div>

        {/* Mode pills */}
        <div className="flex justify-center">
          <div
            className="flex items-center gap-1"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '0.875rem',
              padding: '0.3rem',
            }}
          >
            {BUZZER_MODES.map((item) => {
              const active = item === mode;
              return (
                <button
                  key={item}
                  type="button"
                  onClick={() => onModeChange(item)}
                  aria-pressed={active}
                  title={BUZZER_MODE_LABELS[item].hint}
                  style={{
                    borderRadius: '0.625rem',
                    transition: 'all 0.18s ease',
                    boxShadow: active ? '0 0 14px rgba(14,165,233,0.45)' : undefined,
                  }}
                  className={cn(
                    'px-3 py-1.5 text-sm font-bold sm:px-4 sm:py-2',
                    active ? 'bg-sky-500 text-white' : 'text-slate-400 hover:bg-white/10 hover:text-white'
                  )}
                >
                  {BUZZER_MODE_LABELS[item].title}
                </button>
              );
            })}
          </div>
        </div>
        <p className="text-center text-xs text-sky-200/45">{BUZZER_MODE_LABELS[mode].hint}</p>

        {!hostConnected && (
          <div className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-sm font-semibold text-amber-300">
            Bağlantı yenilənir. Moderator hüquqları bərpa olunana qədər bəzi əmrlər işləməyə bilər.
          </div>
        )}
      </header>

      {/* ── Main ── */}
      <main className="relative z-10 flex flex-1 flex-col gap-5 px-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:px-10">
        {/* Reset controls */}
        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={() => onResetAll()}
            style={{ borderRadius: '0.75rem' }}
            className="inline-flex min-h-[56px] flex-1 items-center justify-center gap-2 bg-red-600 px-6 text-sm font-bold text-white transition hover:bg-red-700"
          >
            <RotateCcw className="h-5 w-5" />
            {isLockout ? 'HAMI ÜÇÜN SIFIRLA' : 'NƏTİCƏLƏRİ SIFIRLA'}
          </button>

          {isLockout && (
            <button
              type="button"
              onClick={() => onResetOthers()}
              disabled={presses.length === 0}
              style={{ borderRadius: '0.75rem' }}
              className="inline-flex min-h-[56px] flex-1 items-center justify-center gap-2 bg-amber-500 px-6 text-sm font-bold text-white transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Ban className="h-5 w-5" />
              DİGƏRLƏRİ ÜÇÜN SIFIRLA
            </button>
          )}
        </div>
        {isLockout && (
          <p className="-mt-2 text-xs text-sky-200/45">
            «Hamı üçün» bütün blokları götürür. «Digərləri üçün» bu dövrədə sıxanları növbəti tam sıfırlamaya qədər
            kənarda saxlayır.
          </p>
        )}

        {scoringOn && (
          <ScoringPanel scoring={scoring} roster={players} onQuestionChange={() => onResetAll(true)} />
        )}

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* Presses */}
          <div style={panelStyle} className="overflow-hidden">
            <div className="flex items-center justify-between border-b border-white/5 px-5 py-3.5">
              <h3 className="font-bold text-emerald-300">
                {isLockout ? 'Düyməni sıxan' : 'Nəticələr (Sıxma ardıcıllığı ilə)'}
              </h3>
              <span className="rounded-full bg-emerald-400/15 px-2.5 py-1 text-xs font-bold text-emerald-300">
                {presses.length} sıxma
              </span>
            </div>
            <div className="flex max-h-[42vh] min-h-[220px] flex-col gap-2 overflow-y-auto p-4">
              {presses.length === 0 ? (
                <div className="flex flex-1 flex-col items-center justify-center p-6 text-center text-white/30">
                  <p>Hələ heç kim düyməni sıxmayıb.</p>
                </div>
              ) : (
                presses.map((p) => (
                  <div
                    key={p.playerName}
                    className={cn(
                      'flex items-center gap-4 rounded-xl border px-5 py-4',
                      p.order === 1
                        ? 'border-amber-400/60 bg-amber-400/20 text-amber-100'
                        : 'border-white/10 bg-white/5 text-white/80'
                    )}
                  >
                    <span
                      className={cn(
                        'text-2xl font-black tabular-nums',
                        p.order === 1 ? 'text-amber-300' : 'text-emerald-300'
                      )}
                    >
                      #{p.order}
                    </span>
                    <span className="flex-1 truncate text-lg font-bold">{p.playerName}</span>
                    {scoringOn && (
                      <VerdictButtons
                        points={p.playerName === answeringPlayer ? scoring.value : null}
                        verdict={getVerdict(scoring.state, p.playerName)}
                        onJudge={(verdict) => handleVerdict(p.playerName, verdict)}
                      />
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Players */}
          <div style={panelStyle} className="overflow-hidden">
            <div className="flex items-center justify-between border-b border-white/5 px-5 py-3.5">
              <h3 className="flex items-center gap-2 font-bold text-white">
                <Users className="h-5 w-5 text-sky-400" />
                İştirakçılar
              </h3>
              <span className="rounded-full bg-sky-400/15 px-2.5 py-1 text-xs font-bold text-sky-300">
                {players.length} nəfər
              </span>
            </div>
            <div className="flex max-h-[42vh] min-h-[220px] flex-col gap-2 overflow-y-auto p-4">
              {players.length === 0 ? (
                <div className="flex flex-1 flex-col items-center justify-center p-6 text-center text-white/30">
                  <AlertTriangle className="mb-3 h-10 w-10 opacity-40" />
                  <p>Hələ heç kim qoşulmayıb.</p>
                  <p className="mt-1 text-xs">Oyunçular kodu daxil edərək qoşula bilərlər.</p>
                </div>
              ) : (
                players.map((p) => {
                  const blocked = lockedOutPlayers.some((n) => n.toLowerCase() === p.toLowerCase());
                  return (
                    <div
                      key={p}
                      className={cn(
                        'flex items-center justify-between rounded-xl border px-4 py-3',
                        blocked ? 'border-rose-400/30 bg-rose-500/10' : 'border-white/10 bg-white/5'
                      )}
                    >
                      <span className={cn('font-semibold', blocked ? 'text-rose-200/70 line-through' : 'text-white')}>
                        {p}
                      </span>
                      {blocked ? (
                        <span className="flex items-center gap-1 text-xs font-bold text-rose-300">
                          <Lock className="h-3.5 w-3.5" />
                          bloklu
                        </span>
                      ) : (
                        <span className="h-2 w-2 rounded-full bg-emerald-400" />
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </main>
    </BuzzerStage>
  );
};

export default BuzzerHost;
